import { Maybe } from '@csp/csp-common-model';
import traverse, { TraverseContext } from 'traverse';
import { UnifyContentPackage } from '@csp/csp-common-content-models/src/lib/UnifyContentPackage';
import { Asset } from '@dhcmss-contentstack/common-api-dto';
import { isAssetMetadata } from '@csp/csp-common-content-models';
import { ContentCacheObject } from '../../model/ContentCacheService';
import { ContentPackageType } from '../../model/ContentPackageType';
import { setCachedPackage } from '../../utils/cacheUtil';
import { getContentClientType, getContentLocale, getContentVersion, getRole } from '../../utils/configUtil';
import { BulkAssetSignedUrls } from '../model/BulkAssetSignedUrls';
import { ContentPackageParams } from '../model/ContentPackageParams';
import { AssetDeliveryService } from '../service/AssetDeliveryService';

export const signedUrlsHaveExpired = (maybeCachedPackage: ContentCacheObject): boolean =>
  !!maybeCachedPackage.signedUrlExpirationTimeUnixMillis &&
  new Date().getTime() > maybeCachedPackage.signedUrlExpirationTimeUnixMillis;

export const fetchSignedAssetUrls = async (
  contentCacheObject: ContentCacheObject,
  packageType: ContentPackageType,
): Promise<UnifyContentPackage> => {
  const [contentPackage, expirationDate] = await maybeFetchAssetUrls(
    {
      packageType,
      clientType: getContentClientType(),
      version: getContentVersion(packageType),
      locale: getContentLocale(),
      role: getRole(),
    },
    contentCacheObject,
  );
  await setCachedPackage(packageType, contentCacheObject.etag, contentPackage, expirationDate);
  return contentPackage;
};

const maybeFetchAssetUrls = async (
  contentPackageDescriptor: ContentPackageParams,
  contentCacheObject: ContentCacheObject,
): Promise<[UnifyContentPackage, Maybe<number>]> => {
  const assetUrls = getAssetPathsFromPackage(contentCacheObject.content);
  if (assetUrls.length > 0) {
    const assetUrlsResponse = await AssetDeliveryService.fetchAssetUrls(contentPackageDescriptor, assetUrls);
    return [setAssetUrls(contentCacheObject.content, assetUrlsResponse.signedUrls), assetUrlsResponse.expirationDate];
  }
  return [contentCacheObject.content, undefined];
};

export const getAssetPathsFromPackage = (contentPackage: UnifyContentPackage): string[] => {
  const assetUrls: string[] = [];

  findAssets(contentPackage, (_context, asset) => {
    if (isAssetMetadata(asset.metadata)) {
      if (asset.metadata.v1) {
        assetUrls.push(asset.metadata.v1.absolutePath);
      }
    } else if (asset.relative_path) {
      /**
       * This is only used to manage backwards compatibility with packages created for Unify 9.0 and 10.0
       * All content packages from 11.0 and onwards use the metadata field for asset paths.
       * This code is also used by the backend.
       */
      assetUrls.push(asset.relative_path);
    }
  });

  return assetUrls;
};

export const isAsset = (maybeAssetNode: unknown): maybeAssetNode is Asset =>
  (maybeAssetNode as Asset)?.filename !== undefined &&
  (maybeAssetNode as Asset)?.content_type !== undefined &&
  (maybeAssetNode as Asset)?.file_size !== undefined;

/**
 * Traverses the entire object 'anyObject' and calls the callback function once for each asset
 * @param anyObject
 * @param callback
 */
export const findAssets = (
  anyObject: object,
  callback: (traverseContext: TraverseContext, asset: Asset) => void,
): void => {
  traverse(anyObject).forEach(function () {
    if (isAsset(this.node)) {
      callback(this, this.node);
    }
  });
};

export const setAssetUrls = (
  contentPackage: UnifyContentPackage,
  assetUrls: BulkAssetSignedUrls,
): UnifyContentPackage => {
  findAssets(contentPackage, (context, asset) => {
    if (isAssetMetadata(asset.metadata)) {
      if (asset.metadata.v1) {
        context.update({ ...context.node, url: assetUrls[asset.metadata.v1.absolutePath] });
      }
    } else if (asset.relative_path) {
      /**
       * This is only used to manage backwards compatibility with packages created for Unify 9.0 and 10.0
       * All content packages from 11.0 and onwards use the metadata field for asset paths.
       * This code is also used by the backend.
       */
      context.update({ ...context.node, url: assetUrls[asset.relative_path] });
    }
  });

  return contentPackage;
};
